import React, { useState, useEffect } from "react";
import { Menu, X, Globe, User, ChevronUp, ChevronDown, Calendar } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "../images/logo/logo.svg";
import LANGS from "../i18n/langs";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { internalWebUrl } from "../config";

const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem("@wirasana_lang");
    const parsed = stored ? JSON.parse(stored) : null;
    if (parsed) return parsed;
  } catch {}
  localStorage.setItem("@wirasana_lang", JSON.stringify(LANGS[0]));
  return LANGS[0];
};

function Navbar2({ darkBackground }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [language, setLanguage] = useState(getStoredLanguage);

  const { t } = useTranslation();
  const location = useLocation();
  const currentLocation = location.pathname;

  const navLinks = [
    { name: "home", path: "/" },
    { name: "vehicles", path: "/vehicles" },
    { name: "about_us", path: "/about-us" },
    { name: "contact_us", path: "/contact" },
    { name: "faq", path: "/faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    i18next.changeLanguage(language.value);
  }, [language]);

  const toggleNav = () => setNavOpen((prev) => !prev);
  const toggleLang = () => setLangOpen((prev) => !prev);

  const selectLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("@wirasana_lang", JSON.stringify(lang));
    i18next.changeLanguage(lang.value);
    setLangOpen(false);
  };

  const LanguageMenu = ({ isMobile = false, onSelect, language }) => {
    const [open, setOpen] = useState(!isMobile);

    const toggleAccordion = () => {
      if (isMobile) setOpen((prev) => !prev);
    };

    return (
      <div
        className={`${
          isMobile ? "pt-4 border-t border-slate-800" : "absolute right-[150px] mt-[24rem] w-48"
        } bg-slate-800 rounded-sm shadow-xl border border-slate-700`}
      >
        {/* Header */}
        {isMobile && (
          <button
            onClick={toggleAccordion}
            className="flex items-center justify-between w-full px-4 py-3 transition-colors text-slate-300 hover:text-white"
          >
            <div className="flex items-center space-x-2">
              <Globe size={18} />
              <span className="text-sm font-medium">Language</span>
            </div>
            {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        )}

        {/* List bahasa */}
        <div
          className={`transition-all duration-300 overflow-hidden ${
            open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className={`${isMobile ? "px-2 pb-3" : ""} space-y-1`}>
            {LANGS.map((lang) => (
              <button
                key={lang.value}
                onClick={() => onSelect(lang)}
                className={`flex items-center w-full px-4 py-2.5 text-left text-white text-sm font-medium rounded-sm transition-all ${
                  language?.value === lang.value ? "bg-[#f5272b] hover:bg-red-600" : "hover:bg-slate-700"
                }`}
              >
                <img src={`/${lang.icon}`} alt={lang.label} className="w-5 h-5 mr-2" />
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || darkBackground ? "bg-slate-900/95 backdrop-blur-lg shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="max-w-[133rem] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="flex items-center justify-center w-12 h-12 p-1 transition-transform duration-300 transform bg-white rounded-md group-hover:scale-110">
                <img src={logo} alt="wirasana logo" className="w-8 h-8" />
              </div>
              <span className="text-2xl font-bold text-white">WIRASANA RENT A CAR</span>
            </Link>

            <div className="items-center hidden space-x-8 md:flex">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={` font-medium text-xl transition-colors duration-300 hover:text-[#f5272b] ${
                    currentLocation === link.path ? "text-[#f5272b]" : "text-white"
                  }`}
                >
                  {t(link.name)}
                </Link>
              ))}
            </div>

            <div className="relative items-center hidden space-x-4 md:flex">
              <button
                onClick={toggleLang}
                className="flex items-center px-4 py-2 space-x-2 text-white transition-all duration-300 rounded-sm hover:bg-slate-800/50"
              >
                <img src={`/${language.icon}`} alt={language.label} className="w-5 h-5" />
                <span className="text-xl font-medium">{language.label}</span>
              </button>

              {langOpen && <LanguageMenu onSelect={selectLanguage} />}
              <Link
                to="/manage-booking"
                className="flex items-center text-xl space-x-2 bg-transparent border border-gray-300 hover:text-gray-700 text-gray-300 hover:bg-gray-100 font-semibold px-6 py-2.5 rounded-sm transition-all duration-300 transform hover:scale-105"
              >
                <Calendar size={18} />
                <span>{t("manage_booking")}</span>
              </Link>

              <Link
                to={internalWebUrl}
                className="flex items-center text-xl space-x-2 bg-[#f5272b] hover:bg-red-600 text-white font-semibold px-6 py-2.5 rounded-sm transition-all duration-300 transform hover:scale-105"
              >
                <User size={18} />
                <span>{t("sign_in")}</span>
              </Link>
            </div>

            <button
              onClick={toggleNav}
              className="p-2 text-white transition-colors rounded-sm md:hidden hover:bg-slate-800/50"
            >
              {navOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        <div
          className={`md:hidden transition-all duration-300 ease-in-out ${
            navOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="border-t bg-slate-900/98 backdrop-blur-lg border-slate-800">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={toggleNav}
                  className={`block font-medium text-lg py-3 px-4 rounded-sm transition-all hover:bg-slate-800/50 hover:text-[#f5272b] ${
                    currentLocation === link.path ? "text-[#f5272b]" : "text-white"
                  }`}
                >
                  {t(link.name)}
                </Link>
              ))}

              <LanguageMenu
                isMobile
                onSelect={(lang) => {
                  selectLanguage(lang);
                  toggleNav();
                }}
              />
              <Link
                to="/manage-booking"
                className="flex items-center justify-center py-4 mt-6 space-x-2 font-semibold text-gray-300 bg-transparent border border-gray-300 rounded-sm hover:text-gray-700 hover:bg-gray-100"
              >
                <Calendar size={20} />
                <span>{t("manage_booking")}</span>
              </Link>
              <Link
                to={internalWebUrl}
                onClick={toggleNav}
                className="flex items-center justify-center space-x-2 bg-[#f5272b] hover:bg-red-600 text-white font-bold py-4 rounded-sm transition-all mt-6"
              >
                <User size={20} />
                <span>{t("sign_in")}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {langOpen && <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />}
    </>
  );
}

export default Navbar2;
